import React, { createContext, useContext, useState, useCallback } from 'react';
import ReactDOM from 'react-dom';
import FormContainer, { useModalContainerRef } from '../Form/FormContainer';

export interface ModalConfig {
    id?: number;
    title?: string;
    component: React.ComponentType<any>;
    props?: Record<string, any>;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    onClose?: (result?: any) => void;
    onSelect?: (data: any) => void;
    containerRef?: React.RefObject<HTMLDivElement> | null;
}

interface ModalStackContextType {
    open: (config: ModalConfig) => void;
    close: (result?: any) => void;
    select: (data: any) => void;
}

const ModalStackContext = createContext<ModalStackContextType | null>(null);

let modalIdCounter = 0;

export const ModalStackProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [modals, setModals] = useState<ModalConfig[]>([]);

    const open = useCallback((config: ModalConfig) => {
        const id = ++modalIdCounter;
        setModals(prev => [...prev, { ...config, id }]);
    }, []);

    const close = useCallback((result?: any) => {
        setModals(prev => {
            if (prev.length === 0) return prev;
            const current = prev[prev.length - 1];
            setTimeout(() => current?.onClose?.(result), 0);
            return prev.slice(0, -1);
        });
    }, []);

    const select = useCallback((data: any) => {
        setModals(prev => {
            if (prev.length === 0) return prev;
            const current = prev[prev.length - 1];
            setTimeout(() => current?.onSelect?.(data), 0);
            return prev.slice(0, -1);
        });
    }, []);

    const renderModal = (modal: ModalConfig) => {
        const Component = modal.component;
        const modalElement = (
            <FormContainer
                key={modal.id}
                show={true}
                onHide={() => close()}
                size={modal.size || 'xl'}
            >
                <FormContainer.Header onClose={() => close()}>
                    <h5>{modal.title || 'Modal'}</h5>
                </FormContainer.Header>
                <FormContainer.Body noPadding>
                    <Component
                        {...modal.props}
                        triggerClose={(result?: any) => close(result)}
                        triggerSelect={(data: any) => select(data)}
                    />
                </FormContainer.Body>
            </FormContainer>
        );

        // Se o modal tem containerRef, renderiza via portal no container pai
        const portalTarget = modal.containerRef?.current;
        if (portalTarget) {
            return ReactDOM.createPortal(modalElement, portalTarget);
        }
        return modalElement;
    };

    return (
        <ModalStackContext.Provider value={{ open, close, select }}>
            {children}
            {modals.map(renderModal)}
        </ModalStackContext.Provider>
    );
};

export const useModalStack = (): ModalStackContextType => {
    const context = useContext(ModalStackContext);
    const containerRef = useModalContainerRef();

    if (!context) {
        throw new Error('useModalStack must be used within a ModalStackProvider');
    }

    // Wrapper que automaticamente adiciona containerRef ao abrir modal
    const openWithContainer = useCallback((config: ModalConfig) => {
        context.open({ ...config, containerRef });
    }, [context, containerRef]);

    return {
        ...context,
        open: openWithContainer
    };
};

export default ModalStackProvider;
